import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { connect, disconnect } from 'echarts'
import ReactECharts from 'echarts-for-react'
import type { DateTime } from 'luxon'

import { ChartNavControls } from '@/components/charts/history/ChartNavControls'
import { buildPeriodOptions, findPeriodOption, formatPeriodLabel } from '@/components/charts/history/periodOptions'
import { type HistoryWindow, useHistoryNavigation } from '@/components/charts/history/useHistoryNavigation'
import { useAuth } from '@/hooks/useAuth'
import { normalizeTimezone } from '@/insights/common/timezone'
import type { ApiService } from '@/types/dashboard-types'

import { useTrackerChartOptions } from './useTrackerChartOptions'

import { TrackerHistory } from '../types'

import '@/insights/assets/historyGraph/echarts-setup'

/** Shared group id so the two canvases zoom and hover together. */
const CONNECT_GROUP = 'tracker-history'

interface TrackerHistoryChartProps {
  data: TrackerHistory
  reload: (api: ApiService, window: HistoryWindow) => Promise<TrackerHistory | undefined>
  from: DateTime
  periodHours: number
  units: 'Imperial' | 'Metric'
  /** Shipment temperature range for min/max marklines. */
  shipmentRange?: { min: number | null; max: number | null }
  /** Range the From/To pickers start on (shipment bounds). Must be memoised by the caller. */
  defaultRange?: HistoryWindow | null
  /** Max height of the scroll region holding both canvases. */
  maxScrollHeight?: string
  /** Where the period and date controls sit; the asset history graph keeps them on top. */
  controlsPosition?: 'top' | 'bottom'
  /** Told the drawn window whenever it changes; see useHistoryNavigation. */
  onViewChange?: (window: HistoryWindow) => void
}

/**
 * Tracker history chart mirroring the asset temperature graph. Temperature is
 * pinned to the top of a scroll region with geofence bands, midnight dividers
 * and shipment range marklines; the battery, humidity and light panels scroll
 * into view beneath it, so temperature stays on screen as the comparison
 * baseline. Shared period navigation (back/forward, jump-to-date, reset,
 * ctrl-scroll zoom) plus a period selector sit below, outside the scroll area.
 */
function TrackerHistoryChart({
  data,
  reload,
  from,
  periodHours,
  units,
  shipmentRange,
  defaultRange,
  maxScrollHeight = '60vh',
  controlsPosition = 'bottom',
  onViewChange,
}: TrackerHistoryChartProps) {
  const tempRef = useRef<ReactECharts>(null)
  const metricsRef = useRef<ReactECharts>(null)
  const zone = normalizeTimezone(useAuth().user?.timezone)

  const {
    currentData,
    loading,
    periodWindow,
    range,
    viewHours,
    isModified,
    navigate,
    jumpToDate,
    resetToDefault,
    applyRange,
    changeViewHours,
  } = useHistoryNavigation<TrackerHistory>({ data, from, periodHours, zone, reload, defaultRange, onViewChange })

  const [isZoomed, setIsZoomed] = useState(false)
  const zoomTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const periodOptions = buildPeriodOptions(range)
  const selectedPeriod = findPeriodOption(periodOptions, viewHours)
  const periodLabel = selectedPeriod?.label ?? formatPeriodLabel(viewHours)

  useEffect(() => {
    setIsZoomed(false)
  }, [periodWindow])

  useEffect(
    () => () => {
      if (zoomTimer.current) clearTimeout(zoomTimer.current)
    },
    []
  )

  const { temp, metrics } = useTrackerChartOptions(currentData, periodWindow, zone, units, shipmentRange)

  const hasAnyData =
    !!currentData &&
    (currentData.temperature.length > 0 ||
      currentData.humidity.length > 0 ||
      currentData.light.length > 0 ||
      currentData.battery.length > 0)

  // `axisPointer.link` only reaches grids inside one instance, so the canvases
  // are grouped instead: echarts re-dispatches zoom and tooltip actions to every
  // chart sharing a group id. Re-runs when either canvas mounts or unmounts.
  useEffect(() => {
    const tempChart = tempRef.current?.getEchartsInstance()
    const metricsChart = metricsRef.current?.getEchartsInstance()
    if (!tempChart || !metricsChart) return
    tempChart.group = CONNECT_GROUP
    metricsChart.group = CONNECT_GROUP
    connect(CONNECT_GROUP)
    return () => disconnect(CONNECT_GROUP)
  }, [hasAnyData, metrics.height])

  const resetZoom = useCallback(() => {
    const instance = tempRef.current?.getEchartsInstance()
    if (instance) {
      instance.dispatchAction({ type: 'dataZoom', dataZoomIndex: 0, start: 0, end: 100 })
    }
    setIsZoomed(false)
  }, [])

  const onEvents = useMemo(
    () => ({
      datazoom: (params: { batch?: { start?: number; end?: number }[]; start?: number; end?: number }) => {
        const batch = params.batch?.[0] ?? params
        if (batch.start != null && batch.end != null) {
          const zoomed = batch.start > 0.5 || batch.end < 99.5
          if (zoomTimer.current) clearTimeout(zoomTimer.current)
          zoomTimer.current = setTimeout(() => {
            setIsZoomed(zoomed)
          }, 200)
        }
      },
      dblclick: () => {
        const instance = tempRef.current?.getEchartsInstance()
        if (instance) {
          instance.dispatchAction({ type: 'dataZoom', dataZoomIndex: 0, start: 0, end: 100 })
        }
        setIsZoomed(false)
      },
    }),
    []
  )

  const controls = (
    <ChartNavControls
      periodLabel={periodLabel}
      periodWindow={periodWindow}
      range={range}
      from={from}
      zone={zone}
      isModified={isModified}
      isZoomed={isZoomed}
      onResetZoom={resetZoom}
      navigate={navigate}
      jumpToDate={jumpToDate}
      resetToDefault={resetToDefault}
      showRange
      onRangeChange={applyRange}
      zoomHint="Shift + Scroll to Zoom"
    >
      <select
        className="select select-xs w-28 text-xs"
        value={selectedPeriod?.value ?? String(viewHours)}
        onChange={(e) => changeViewHours(Number(e.target.value))}
        title="Period"
      >
        {periodOptions.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </ChartNavControls>
  )

  return (
    <div>
      {controlsPosition === 'top' && controls}
      {/* A maximum, not a fixed height: with only the temperature panel (a cold storage
          sensor) a fixed 60vh left a tall blank below it with the controls at the bottom. */}
      <div className="relative" style={{ maxHeight: maxScrollHeight }}>
        {hasAnyData ? (
          <div className="overflow-y-auto" style={{ maxHeight: maxScrollHeight }}>
            {/* The opaque background is load-bearing: without it the metric
                panels scroll visibly through the pinned temperature chart. */}
            <div className="sticky top-0 z-10 bg-base-100">
              <ReactECharts
                ref={tempRef}
                option={temp.option}
                notMerge={true}
                lazyUpdate={true}
                onEvents={onEvents}
                opts={{ width: 'auto', height: 'auto' }}
                style={{ height: temp.height, width: '100%' }}
              />
            </div>
            {metrics.height > 0 && (
              <ReactECharts
                ref={metricsRef}
                option={metrics.option}
                notMerge={true}
                lazyUpdate={true}
                opts={{ width: 'auto', height: 'auto' }}
                style={{ height: metrics.height, width: '100%' }}
              />
            )}
          </div>
        ) : (
          <div className="flex h-48 items-center justify-center text-sm text-base-content/60">
            {loading ? 'Loading history…' : 'No history for this period'}
          </div>
        )}
        {loading && hasAnyData && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/60">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600" />
          </div>
        )}
      </div>

      {controlsPosition === 'bottom' && controls}
    </div>
  )
}

export default memo(TrackerHistoryChart)
